import CommonList from "@/components/common/List";
import CommonTableList from "@/components/common/TableList";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVerticalIcon } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import ReservationCreate from "./Create";
import ReservationDetail from "./ReservationDetail";
import ReservationEdit from "./Edit";
import ReservationDelete from "./Delete";
import { handleFormChange } from "@/utils/formChange";
import DialogConfirmCommon from "@/components/common/DialogConfirm";
import DatePicker from "@/components/input/DatePicker";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format, subYears } from "date-fns";
import { ResourceContextProvider } from "react-admin";
import { Reservation } from "@/interface/entityInterface";
import axiosInstance from "@/utils/axiosInstance";

const ReservationList = () => {
  const [isDetailOpen, setIsDetailsOpen] = useState(false);
  const [row, setRow] = useState<Reservation | null>(null);
  const [props, setProp] = useState<{date: Date, status: string}>({ date: new Date(), status: "ALL" });
  const [data, setData] = useState([]);

  useEffect(() => {
    const getData = async () => {
      const { data: users } = await axiosInstance.get(`/users`);
      setData(users);
    };

    getData();
  }, []);

  const handleOpen = (e: FormEvent<HTMLInputElement>, row: Reservation) => {
    if (e.target.role && e.target?.role == "checkbox") {
      return;
    }
    setRow(row);
    setIsDetailsOpen(true);
  };

  const columns: ColumnDef<Reservation>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ row }) => <div className="capitalize">{row.getValue("id")}</div>,
    },
    {
      accessorKey: "branches",
      header: "Branch",
      cell: ({ row }) => {
        return (
          <div className="flex flex-col text-sm space-y-1">
            <span className="font-medium">{row.original.branches?.name}</span>
          </div>
        );
      },
    },
    {
      id: "client",
      accessorFn: (row) => row.guest_user?.name || "",
      header: "Client",
      filterFn: "includesString",
      cell: ({ row }) => {
        return (
          <div className="flex flex-col text-sm space-y-1">
            <span className="font-medium">
              {row?.original?.guest_user?.name}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "vehicleRef",
      header: "Vehicle",
      cell: ({ row }) => {
        return (
          <div className="flex flex-col text-sm space-y-1">
            <span className="font-medium">
              {row.original.guest_vehicle.license_plate}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "technicianRef",
      header: "Technician",
      cell: ({ row }) => {
        const { technician } = row.original;
        return (
          <DialogConfirmCommon
            id={row.original.id}
            endpoint="auto-service-reservation"
            endpoint2="assignTechnician"
            selectedValue={technician?.id}
            description={`Are you sure you want to assign this technician?`}
            dataOptions={data}
            dataProp="firstname"
            propKey="technician_id"
          />
        );
      },
    },
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row }) => {
        return (
          <div className="flex flex-col text-sm space-y-1">
            <span className="font-medium">
              {row.getValue("date")
                ? format(row.getValue("date"), "dd MMMM yyyy HH:mm")
                : "-"}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        return (
          <DialogConfirmCommon
            id={row.original.id}
            endpoint="auto-service-reservation"
            endpoint2="assignStatus"
            dataOptions={["PENDING", "IN_PROGRESS", "COMPLETED", "CANCELLED"]}
            selectedValue={row.getValue("status")}
            description="Are you sure you want to change the status?"
            propKey="status"
          />
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
              size="icon"
            >
              <MoreVerticalIcon />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-32">
            <ReservationEdit prop={row.original.id} />
            <DropdownMenuSeparator />
            <ReservationDelete prop={row.original.id} />
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <ResourceContextProvider value="auto-service-reservation">
      <div className="w-full max-w-[180px] mt-6">
        <DatePicker
          value={props.date}
          setValue={(e: FormEvent<unknown>) => handleFormChange(e, setProp)}
          minDate={subYears(new Date(), 1)}
        />
      </div>
      <div className="w-full max-w-md mt-6">
        <Tabs
          className="w-full"
          value={props.status}
          onValueChange={(val: any) => {
            const fakeEvent = {
              currentTarget: {
                id: "status",
                value: val,
              },
            };

            handleFormChange(fakeEvent, setProp);
          }}
        >
          <TabsList className="w-full">
            <TabsTrigger value="ALL">All</TabsTrigger>
            <TabsTrigger value="PENDING">PENDING</TabsTrigger>
            <TabsTrigger value="IN_PROGRESS">IN PROGRESS</TabsTrigger>
            <TabsTrigger value="COMPLETED">FINISHED</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <CommonList
        filters={
          props.status === "ALL"
            ? {
                date: props.date,
              }
            : props
        }
      >
        <CommonTableList
          columns={columns}
          filterSearch="client"
          create={<ReservationCreate />}
          rowClick={handleOpen}
        />
        {isDetailOpen && (
          <ReservationDetail
            open={isDetailOpen}
            setOpen={setIsDetailsOpen}
            row={row}
          />
        )}
      </CommonList>
    </ResourceContextProvider>
  );
};

export default ReservationList;
